import type {
  CreatePaymentAuthorizationRequest,
  PaymentAuthorizationResult,
  PaymentProfileOwner,
  PaymentSetupAttempt,
  PaymentSetupAttemptUpdateRequest,
} from '@billing/payment-sdk'
import type { Stripe, StripeCardNumberElement, StripeElements } from '@stripe/stripe-js'
import type { Offer } from '~/api/pricing-offers/models'
import { definePaymentSdkClient } from '@billing/payment-sdk'
import * as Sentry from '@sentry/vue'
import { loadStripe } from '@stripe/stripe-js'
import { FetchError } from 'ofetch'

const POLL_INTERVAL_MS = 1500
const POLL_MAX_ATTEMPTS = 20

/**
 * Creation request for POST /customer/payments/setup-attempts/
 * Matches the domain-models PaymentSetupAttemptRequest DTO exactly.
 *
 * Required fields (server validates via @NotNull):
 *   - context
 *   - paymentServiceProvider
 *
 * All other fields are optional (@Nullable).
 * Note: customerIdentifier is always overwritten server-side from the JWT.
 */
export interface PreBookingSetupAttemptRequest {
  context: 'PRE_BOOKING'
  paymentServiceProvider: string
  legalEntityId?: number
  profileId?: number
  reference?: string
  referenceType?: 'PRE_BOOKING_OFFER'
}

/**
 * Wraps the full payment setup lifecycle for the pre-booking onboarding flow.
 *
 * All API calls go through internal Nuxt server routes so the JWT never
 * reaches the browser (the server routes read the HttpOnly 'kcToken' cookie).
 *
 * Lifecycle:
 *  1. createSetupAttempt()  → creates backend attempt, returns clientSecret
 *  2. initStripe()          → loads Stripe SDK with platform publishable key
 *  3. createElements()      → initialises Stripe Elements with the clientSecret
 *  4. mountCardElements()   → renders cardNumber, cardExpiry, cardCvc into DOM nodes
 *  5. confirmCardSetup()    → submits with cardholder name; Stripe handles SCA
 *  6. pollSetupAttempt()    → polls backend until status SUCCEEDED (or failure)
 *  7. createAuthorization() → pre-authorizes the offer amount on the payment method
 */
export function usePaymentSetup() {
  // ─── SDK client — instantiated once per composable call ──────────────────
  const sdkClient = definePaymentSdkClient({
    bearerToken: 'cookie-based',
    baseUrl: '/rental/booking',
    log: { level: 'debug' },
    onRedirectComplete: (result: PaymentAuthorizationResult) => {
      if (result.error) {
        Sentry.captureMessage(`Payment authorization redirect failed: ${result.error.message}`, 'error')
        usePaymentSetupStore().errorMessage = result.error.message
      }
    },
  })

  // ─── Setup Attempt API ────────────────────────────────────────────────────

  const createSetupAttempt = async (body: PreBookingSetupAttemptRequest): Promise<PaymentSetupAttempt | null> => {
    try {
      return await $fetch<PaymentSetupAttempt>('/api/billing/setup-attempts', {
        method: 'POST',
        body,
      })
    }
    catch (error: unknown) {
      if (error instanceof FetchError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          Sentry.captureMessage(`Authorization error while creating setup attempt: ${error.message}`, 'error')
          return null
        }
      }
      Sentry.captureException(error)
      return null
    }
  }

  const getSetupAttempt = async (attemptId: number): Promise<PaymentSetupAttempt | null> => {
    try {
      return await $fetch<PaymentSetupAttempt>(`/api/billing/setup-attempts/${attemptId}`, {
        method: 'GET',
      })
    }
    catch (error: unknown) {
      if (error instanceof FetchError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          Sentry.captureMessage(`Authorization error while fetching setup attempt: ${error.message}`, 'error')
          return null
        }
        else if (error.response?.status === 404) {
          Sentry.captureMessage(`Setup attempt not found: ${attemptId}`, 'warning')
          return null
        }
      }
      Sentry.captureException(error)
      return null
    }
  }

  const updateSetupAttempt = async (attemptId: string, body: PaymentSetupAttemptUpdateRequest): Promise<PaymentSetupAttempt | null> => {
    try {
      return await $fetch<PaymentSetupAttempt>(`/api/billing/setup-attempts/${attemptId}`, {
        method: 'PATCH',
        body,
      })
    }
    catch (error: unknown) {
      if (error instanceof FetchError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          Sentry.captureMessage(`Authorization error while updating setup attempt: ${error.message}`, 'error')
          return null
        }
        else if (error.response?.status === 404) {
          Sentry.captureMessage(`Setup attempt not found for update: ${attemptId}`, 'warning')
          return null
        }
      }
      Sentry.captureException(error)
      return null
    }
  }

  // ─── Stripe Lifecycle ────────────────────────────────────────────────────

  /**
   * Loads the Stripe.js SDK using the platform publishable key.
   * No stripeAccount needed — we only use the platform account for setup.
   */
  const initStripe = async (publishableKey: string): Promise<Stripe | null> => {
    try {
      const stripe = await loadStripe(publishableKey)
      if (!stripe) {
        Sentry.captureMessage('Failed to load Stripe SDK: loadStripe returned null', 'error')
      }
      return stripe
    }
    catch (error: unknown) {
      Sentry.captureException(error)
      return null
    }
  }

  /**
   * Creates a Stripe Elements instance bound to the setup attempt's clientSecret.
   * Must be called after initStripe() and after the clientSecret is available
   * from createSetupAttempt().
   *
   * Appearance tokens sourced from @camelot/tailwind-config:
   *   --color-rebel-100  #5133E6  (brand/primary)
   *   --color-black-100  #1E0046  (text)
   *   --color-black-60   #786690  (placeholder / label muted)
   *   --font-web         -apple-system, BlinkMacSystemFont, Roboto, sans-serif
   *
   * Note: fontFamily must be explicit — 'inherit' does not work inside Stripe's iframe.
   */
  const createElements = (stripe: Stripe, clientSecret: string): StripeElements => {
    return stripe.elements({
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          // Brand & text colours
          colorPrimary: '#5133E6',
          colorText: '#1E0046',
          colorTextPlaceholder: '#786690',
          colorBackground: '#FFFFFF',
          colorDanger: '#E4002B',
          // Typography — explicit stack required (no CSS inheritance inside iframe)
          fontFamily: '-apple-system, BlinkMacSystemFont, Roboto, sans-serif',
          fontSizeBase: '16px',
          fontWeightNormal: '400',
          fontWeightMedium: '500',
          // Layout
          borderRadius: '8px',
          spacingUnit: '4px',
        },
        rules: {
          '.Input': {
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '16px',
            lineHeight: '24px',
            color: '#1E0046',
          },
          '.Input:focus': {
            borderColor: '#5133E6',
            boxShadow: '0 0 0 1px #5133E6',
            outline: 'none',
          },
          '.Input--invalid': {
            borderColor: '#E4002B',
            boxShadow: '0 0 0 1px #E4002B',
          },
          '.Label': {
            fontSize: '14px',
            fontWeight: '500',
            color: '#1E0046',
            marginBottom: '6px',
          },
          '.Error': {
            fontSize: '12px',
            color: '#E4002B',
          },
          '.Tab': {
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            color: '#1E0046',
          },
          '.Tab:hover': {
            color: '#5133E6',
            borderColor: '#5133E6',
          },
          '.Tab--selected': {
            borderColor: '#5133E6',
            color: '#5133E6',
            boxShadow: '0 0 0 1px #5133E6',
          },
        },
      },
    })
  }

  /**
   * Confirms the SetupIntent using the cardNumber element and cardholder name.
   * Stripe handles SCA/3DS automatically.
   *
   * Returns the error message string on failure, or undefined on success.
   */
  const confirmCardSetup = async (
    stripe: Stripe,
    clientSecret: string,
    cardNumber: StripeCardNumberElement,
    cardholderName: string,
    countryCode?: string,
  ): Promise<string | undefined> => {
    const { error } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardNumber,
        billing_details: {
          name: cardholderName,
          ...(countryCode ? { address: { country: countryCode.toUpperCase() } } : {}),
        },
      },
    })

    if (error) {
      Sentry.captureMessage(`Stripe confirmCardSetup error: ${error.message}`, 'error')
      return error.message ?? translate('payment_setup_failed')
    }

    return undefined
  }

  /**
   * Polls GET /api/billing/setup-attempts/:id every POLL_INTERVAL_MS ms
   * until the status is SUCCEEDED, or until POLL_MAX_ATTEMPTS is reached.
   *
   * Resolves with the final PaymentSetupAttempt on SUCCEEDED.
   * Rejects with an error message on FAILED, PROVIDER_FAILED, or timeout.
   *
   * Pass an optional `isCancelled` callback to abort polling when the
   * component unmounts. On cancellation the promise resolves silently
   * (no error is surfaced — the user navigated away intentionally).
   */
  const pollSetupAttempt = (attemptId: number, isCancelled: () => boolean): Promise<PaymentSetupAttempt> => {
    return new Promise((resolve, reject) => {
      let attempts = 0

      const poll = async () => {
        if (isCancelled()) {
          resolve({ status: 'CANCELED' } as PaymentSetupAttempt)
          return
        }

        attempts++
        const attempt = await getSetupAttempt(attemptId)

        if (!attempt) {
          reject(new Error(translate('payment_setup_failed')))
          return
        }

        if (attempt.status === 'SUCCEEDED') {
          resolve(attempt)
          return
        }

        if (attempt.status === 'FAILED' || attempt.status === 'PROVIDER_FAILED' || attempt.status === 'CANCELED') {
          Sentry.captureMessage(`Setup attempt ${attemptId} ended with status: ${attempt.status}`, 'error')
          reject(new Error(translate('payment_setup_failed')))
          return
        }

        if (attempts >= POLL_MAX_ATTEMPTS) {
          Sentry.captureMessage(`Setup attempt ${attemptId} polling timed out after ${POLL_MAX_ATTEMPTS} attempts`, 'error')
          reject(new Error(translate('payment_setup_failed')))
          return
        }

        // Status is still INITIATED / PROVIDER_SUCCEEDED — keep polling
        setTimeout(poll, POLL_INTERVAL_MS)
      }

      setTimeout(poll, POLL_INTERVAL_MS)
    })
  }

  // ─── Authorization via @billing/payment-sdk ─────────────────────────────

  /**
   * Builds the pre-booking authorization request from offer + profile owner
   * and calls the SDK client. Keeps the store action free of body construction.
   *
   * The SDK handles SCA (3DS popup or redirect) automatically and returns
   * { paymentAuthorization } on success or { error } on failure.
   */
  const createPreBookingAuthorization = (
    amount: number,
    offer: Offer,
    profileOwner: PaymentProfileOwner,
    bookingLegalEntity: number,
  ): Promise<PaymentAuthorizationResult> => {
    return sdkClient.createPaymentAuthorization({
      amount,
      currency: offer.currency as CreatePaymentAuthorizationRequest['currency'],
      paymentProfileUuid: profileOwner.defaultProfile?.paymentProfileUuid,
      legalEntityId: bookingLegalEntity,
      transactionReferenceType: 'PREBOOKING_UUID',
      requestDetails: {
        type: 'PREBOOKING',
        offerConfigId: String(offer.offerConfigId ?? ''),
        offerIdentifier: offer.offerIdentifier ?? '',
        offerName: offer.buildSeries ?? '',
      },
    })
  }

  return {
    createSetupAttempt,
    getSetupAttempt,
    updateSetupAttempt,
    initStripe,
    createElements,
    confirmCardSetup,
    pollSetupAttempt,
    createPreBookingAuthorization,
  }
}
